import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

const publicPaths = ['/login', '/forgot-password'];

export function AuthGuard({ children, requireAuth = true }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (!loading) {
      const isPublicPath = publicPaths.includes(pathname);
      
      // Redirect authenticated users away from auth pages
      if (user && isPublicPath) {
        navigate('/');
        return;
      }
      
      // Redirect unauthenticated users to login (except for public paths)
      if (!user && requireAuth && !isPublicPath) {
        navigate('/login');
        return;
      }
    }
  }, [user, loading, pathname, navigate, requireAuth]);

  // Show loading screen while checking auth
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For public paths or authenticated users, render children
  const isPublicPath = publicPaths.includes(pathname);
  if (isPublicPath || user || !requireAuth) {
    return <>{children}</>;
  }

  // This shouldn't happen due to redirect above, but just in case
  return null;
}